import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  BrainCircuit,
  Phone,
  PhoneOff,
  MessageSquare,
  CheckCircle2,
  AlertTriangle,
  Search,
  Send,
  FileText,
  Clock,
  Zap,
} from "lucide-react";
import { allFunctions, pillColors } from "./AIFunctionsSlide";

const typeIcon = {
  reason: BrainCircuit,
  action: CheckCircle2,
  call: Phone,
  call_end: PhoneOff,
  sms: MessageSquare,
  check: Search,
  send: Send,
  file: FileText,
  wait: Clock,
  warn: AlertTriangle,
  result: Zap,
};

const typeStyle = {
  reason: "bg-blue-50 border-blue-100 text-blue-700",
  action: "text-slate-700",
  call: "bg-emerald-50 border-emerald-100 text-emerald-700",
  call_end: "text-slate-500",
  sms: "bg-sky-50 border-sky-100 text-sky-700",
  check: "text-slate-600",
  send: "text-slate-600",
  file: "text-slate-600",
  wait: "text-amber-600",
  warn: "bg-amber-50 border-amber-100 text-amber-700",
  result: "bg-emerald-50 border-emerald-100 text-emerald-700 font-semibold",
};

const WorkflowLine = ({ item, index }) => {
  const Icon = typeIcon[item.type] || CheckCircle2;
  const style = typeStyle[item.type] || "text-slate-600";
  const isBlock = ["reason", "call", "sms", "warn", "result"].includes(item.type);

  if (isBlock) {
    return (
      <motion.div
        initial={{ opacity: 0, x: -6 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.03, duration: 0.2 }}
        className={`rounded-lg border px-3 py-2 ${style}`}
      >
        <div className="flex items-start gap-2">
          <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 opacity-70" />
          <span className="text-[11px] leading-relaxed">{item.text}</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: -6 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03, duration: 0.2 }}
      className="flex items-start gap-2 px-1"
    >
      <Icon className="w-3 h-3 flex-shrink-0 mt-0.5 text-slate-400" />
      <span className={`text-[11px] leading-relaxed ${style}`}>{item.text}</span>
    </motion.div>
  );
};

const Popover = ({ func, colors, anchorRef, onMouseEnter, onMouseLeave }) => {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const popW = 380;
    const popH = 420;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow > popH + 8
        ? rect.bottom + 6
        : Math.max(8, rect.top - popH - 6);
    const left = Math.min(rect.left, window.innerWidth - popW - 12);
    setPos({ top, left: Math.max(8, left) });
  }, [anchorRef]);

  return ReactDOM.createPortal(
    <motion.div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      initial={{ opacity: 0, y: 6, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 6, scale: 0.96 }}
      transition={{ duration: 0.15 }}
      style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }}
      className="w-[380px] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
    >
      <div
        className={`px-4 py-2.5 ${colors.bg} border-b ${colors.border} flex items-center gap-2`}
      >
        <BrainCircuit className="w-3.5 h-3.5" style={{ color: colors.accent }} />
        <span className="text-[12px] font-bold text-slate-800 flex-1">{func.text}</span>
        <span
          className="text-[9px] font-bold px-2 py-0.5 rounded-full text-white"
          style={{ backgroundColor: colors.accent }}
        >
          {func.workflow.length} steps
        </span>
      </div>
      <div className="px-3 py-2.5 max-h-[360px] overflow-y-auto space-y-1.5">
        {func.workflow.map((item, i) => (
          <WorkflowLine key={i} item={item} index={i} />
        ))}
      </div>
    </motion.div>,
    document.body
  );
};

const GlowPill = ({ func, colors, index, totalCount }) => {
  const [isOpen, setIsOpen] = useState(false);
  const btnRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 200);
  };

  // Stagger the animation delay so the glow sweeps across pills
  const delay = (index / totalCount) * 3;

  return (
    <>
      <motion.div
        ref={btnRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 + index * 0.015, duration: 0.3 }}
        className={`relative inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-left cursor-default ${colors.bg} ${colors.border} shadow-sm`}
      >
        {/* Animated gradient glow border */}
        <motion.div
          className="absolute inset-0 rounded-lg"
          style={{
            background: `linear-gradient(90deg, transparent, ${colors.accent}40, ${colors.accent}60, ${colors.accent}40, transparent)`,
            backgroundSize: "200% 100%",
          }}
          initial={{ backgroundPosition: "200% 0", opacity: 0 }}
          animate={{
            backgroundPosition: ["-200% 0", "200% 0"],
            opacity: [0, 0.7, 0],
          }}
          transition={{
            duration: 2,
            delay: delay,
            repeat: Infinity,
            repeatDelay: 4,
            ease: "easeInOut",
          }}
        />
        <span className="relative text-[12px] font-medium text-slate-700 whitespace-nowrap">
          {func.text}
        </span>
        <span
          className={`relative text-[9px] font-bold px-1.5 py-0.5 rounded ${colors.dot} text-white`}
        >
          {func.workflow.length}
        </span>
      </motion.div>
      <AnimatePresence>
        {isOpen && (
          <Popover
            func={func}
            colors={colors}
            anchorRef={btnRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        )}
      </AnimatePresence>
    </>
  );
};

const AIFunctionsAllValueSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-5 lg:p-6 relative overflow-hidden font-sans">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100 rounded-full blur-[120px] opacity-40 translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-50 rounded-full blur-[100px] opacity-40 -translate-x-1/2 translate-y-1/2" />

      <div className="relative z-10 flex flex-col h-full min-h-0">
        {/* Header */}
        <div className="mb-3 flex-shrink-0">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
              Asha's AI does <span className="text-blue-600">ALL</span> of
              these <span className="text-blue-600">40+</span> tasks for{" "}
              <span className="text-blue-600">{"<"}$15 per patient</span>.
            </h2>
          </motion.div>
        </div>

        {/* Tile grid with animated glow */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="flex flex-wrap gap-2.5">
            {allFunctions.map((func, i) => (
              <GlowPill
                key={i}
                func={func}
                colors={pillColors[i % pillColors.length]}
                index={i}
                totalCount={allFunctions.length}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIFunctionsAllValueSlide;
